import type { RouteRecordRaw } from 'vue-router'
import { captureException } from '@sentry/vue'
import { log } from '@/services/logging-service'
import { getUpdatePaymentURL } from '@/lib/purchase-utils'
import {
  completeSignin,
  completeSignoutSilent,
} from '@/services/identity-client'
import AuthError from '@/components/auth/auth-error.vue'
import split from '@/lib/split'
import { storeResolutionEvent } from '@/services/identity-events'

const routes: RouteRecordRaw[] = [
  {
    path: '/update-payment.html',
    name: 'update-payment',
    redirect: () => {
      const url = getUpdatePaymentURL()
      window.open(url, '_self')
      return url
    },
  },
  {
    path: '/incoming-share',
    name: 'incomingShare',
    component: () => import('@/pages/incoming-share.vue'),
    meta: {
      preAuthRoute: true,
    },
    // FUTURE: Remove beforeEnter and split.initialize() when gd_disable_onx_trial split is removed
    beforeEnter() {
      split.initialize()
    },
  },
  {
    path: '/auth/callback',
    name: 'identity-callback',
    component: AuthError,
    meta: {
      preAuthRoute: true,
    },
    async beforeEnter() {
      try {
        const user = (await completeSignin()) as
          | {
              state?: { after_login?: string }
            }
          // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
          | void
        log('identity_login_success')
        // User is only returned by redirect signin
        // signinPopup will not return a user
        if (user) {
          // store data to be read in a vertical to send login events
          storeResolutionEvent()
          // Goto deeplink with hard navigation
          // soft nav with return will cause bugs since the app currently expects
          // a hard nav after user is logged in
          const deeplink = user?.state?.after_login ?? '/'
          /**
           * If this changes from a page transition to a router nav,
           * we will need to update the marketing event
           * that indicates auth success.
           *
           * @see app.vue::created
           */
          document.location.replace(deeplink)
        }

        return false
      } catch (error) {
        captureException(error, {
          tags: {
            identity: 'callback',
          },
        })
        log('identity_login_error')
        // Display auth error page
        return true
      }
    },
  },
  {
    path: '/auth/silent-renew',
    name: 'identity-silent-renew',
    component: AuthError,
    meta: {
      preAuthRoute: true,
    },
    async beforeEnter() {
      try {
        // false to disable silent renew to prevent infinite loop of silent renews
        await completeSignin(false)
      } catch (error) {
        captureException(error, {
          tags: {
            identity: 'silent-renew',
          },
        })
        log('identity_silent_renew_error')
      }
      return false
    },
  },
  {
    path: '/auth/post-logout',
    name: 'identity-post-logout',
    component: AuthError,
    meta: {
      preAuthRoute: true,
    },
    async beforeEnter() {
      try {
        await completeSignoutSilent()
        return false
      } catch (error) {
        captureException(error, {
          tags: {
            identity: 'post-logout',
          },
        })
        log('identity_logout_error')
        return '/'
      }
    },
  },
]

export default routes
